import { useEffect, useState, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { kycApi } from "@/api/endpoints/kyc";
import type { KYCStatusResponse } from "@/types/kyc";

interface KYCProtectedRouteProps {
  children: React.ReactNode;
}

// Shared cache to prevent multiple API calls across route changes
let kycStatusCache: KYCStatusResponse | null = null;
let kycStatusPromise: Promise<KYCStatusResponse | null> | null = null;

export default function KYCProtectedRoute({ children }: KYCProtectedRouteProps) {
  const location = useLocation();
  const [kycStatus, setKycStatus] = useState<KYCStatusResponse | null>(kycStatusCache);
  const [loading, setLoading] = useState(!kycStatusCache);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load if we haven't loaded yet and don't have cached data
    if (hasLoadedRef.current || kycStatusCache) {
      if (kycStatusCache) {
        setKycStatus(kycStatusCache);
        setLoading(false);
      }
      return;
    }

    // If there's already a pending request, wait for it
    if (kycStatusPromise) {
      kycStatusPromise.then((status) => {
        if (status) {
          setKycStatus(status);
          setLoading(false);
        }
      });
      return;
    }

    // Mark as loading
    hasLoadedRef.current = true;
    setLoading(true);

    // Create a single promise that will be reused
    kycStatusPromise = (async () => {
      try {
        const status = await kycApi.getStatus();
        kycStatusCache = status;
        setKycStatus(status);
        setLoading(false);
        return status;
      } catch (error) {
        console.error('Failed to load KYC status:', error);
        setLoading(false);
        return null;
      } finally {
        kycStatusPromise = null;
      }
    })();
  }, []);

  // Don't show loading screen, just render children while checking
  // This prevents the black screen with loading text
  if (loading || !kycStatus) {
    return <>{children}</>;
  }

  // Allow access to KYC and eSign pages themselves
  if (location.pathname === '/user/kyc' || location.pathname === '/user/esign') {
    return <>{children}</>;
  }

  // Check if KYC is required - redirect if deadline passed and not approved
  // If isExpired is undefined, calculate it based on daysRemaining
  const isExpired = kycStatus.isExpired ?? (kycStatus.daysRemaining !== null && kycStatus.daysRemaining !== undefined && kycStatus.daysRemaining <= 0);
  const kycRequired = isExpired && kycStatus.status !== 'approved';
  
  // Check signature deadline if KYC is approved
  // Only redirect if status is "sent" and deadline has passed
  let signatureRequired = false;
  if (kycStatus.status === 'approved' && kycStatus.signatureDeadline && kycStatus.signature) {
    const signatureDeadline = new Date(kycStatus.signatureDeadline);
    const now = new Date();
    const signatureExpired = signatureDeadline.getTime() <= now.getTime();
    const signatureStatus = kycStatus.signature.status;
    
    // Only require signature if status is "sent" and deadline has passed
    signatureRequired = signatureExpired && 
                       !kycStatus.signatureIsCompleted && 
                       signatureStatus === 'sent';
  }
   
  // Priority: KYC deadline takes precedence over signature deadline
  // Redirect to KYC page if deadline has passed and KYC is not approved
  if (kycRequired) {
    return <Navigate to="/user/kyc" replace />;
  }

  // Redirect to eSign page if signature deadline has passed and signature is not completed
  if (signatureRequired) {
    return <Navigate to="/user/esign" replace />;
  }

  // Allow access for all other cases
  return <>{children}</>;
}

// Component to enforce KYC on specific routes that require approval
interface KYCApprovalRequiredProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

export function KYCApprovalRequired({ 
  children, 
  fallbackMessage = "KYC approval is required to access this feature." 
}: KYCApprovalRequiredProps) {
  const [kycStatus, setKycStatus] = useState<KYCStatusResponse | null>(kycStatusCache);
  const [loading, setLoading] = useState(!kycStatusCache);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Use cached data if available
    if (kycStatusCache) {
      setKycStatus(kycStatusCache);
      setLoading(false);
      return;
    }

    // If already loading, wait for the existing promise
    if (kycStatusPromise) {
      kycStatusPromise.then((status) => {
        if (status) {
          setKycStatus(status);
          setLoading(false);
        }
      });
      return;
    }

    // If we've already loaded in this component instance, don't reload
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;
    setLoading(true);

    // Create or reuse the promise
    kycStatusPromise = (async () => {
      try {
        const status = await kycApi.getStatus();
        kycStatusCache = status;
        setKycStatus(status);
        setLoading(false);
        return status;
      } catch (error) {
        console.error('Failed to load KYC status:', error);
        setLoading(false);
        return null;
      } finally {
        kycStatusPromise = null;
      }
    })();
  }, []);

  // Don't show loading, just return null to prevent UI disruption
  if (loading) {
    return null;
  }

  // Only allow access if KYC is approved
  if (kycStatus?.status !== 'approved') {
    return (
      <div className="w-full h-64 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="text-orange-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-black dark:text-white mb-2">
            KYC Verification Required
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {fallbackMessage}
          </p>
          {kycStatus?.status === 'not_submitted' && (
            <button
              onClick={() => window.location.href = '/user/kyc'}
              className="px-4 py-2 bg-gradient-to-r from-skyBright to-teal text-black font-medium hover:opacity-90"
            >
              Complete KYC
            </button>
          )}
          {kycStatus?.status === 'pending' && (
            <p className="text-yellow-600 dark:text-yellow-400">
              Your KYC is under review. Please wait for approval.
            </p>
          )}
          {kycStatus?.status === 'rejected' && (
            <button
              onClick={() => window.location.href = '/user/kyc'}
              className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-medium hover:opacity-90"
            >
              Resubmit KYC
            </button>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}